import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Coins, Sparkles, ChevronLeft, Zap, Crown, Wallet, Gift, TrendingUp, Gem, PartyPopper } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import GiftIcon from "../components/GiftIcon";

const FEATURED_GIFT_IDS = [
  "gift_burger",
  "gift_dessert_tower",
  "gift_food_truck",
  "gift_hidden_gem",
  "gift_diamond_crown",
  "gift_hidden_feast",
];

// Confetti icon pool — vector, no emojis (Phase 1 no-emoji sweep)
const CONFETTI_ICONS = [Gem, Sparkles, Coins, PartyPopper, Wallet];

function PurchaseCelebration({ credits, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 4200);
    return () => clearTimeout(t);
  }, [onDone]);
  const confetti = Array.from({ length: 40 }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 1.4,
    duration: 2.5 + Math.random() * 1.5,
    size: 18 + Math.random() * 24,
    Icon: CONFETTI_ICONS[Math.floor(Math.random() * CONFETTI_ICONS.length)],
    hue: ["#F472B6", "#22D3EE", "#FDE047", "#A78BFA", "#34D399"][Math.floor(Math.random() * 5)],
  }));
  return (
    <div className="fixed inset-0 z-[95] grid place-items-center bg-zinc-950/85 backdrop-blur-sm">
      {confetti.map((c, i) => (
        <span
          key={i}
          className="absolute top-0"
          style={{
            left: `${c.left}%`,
            color: c.hue,
            animation: `feastDrop ${c.duration}s ${c.delay}s ease-in forwards`,
          }}
        >
          <c.Icon size={c.size} strokeWidth={1.75} />
        </span>
      ))}
      <div className="text-center">
        <div className="gift-diamond-shine inline-block text-cyan-200" style={{ filter: "drop-shadow(0 0 40px rgba(103,232,249,0.55))" }}>
          <Gem size={180} strokeWidth={1.2} />
        </div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-pink-300 font-black">Credits added</p>
        <h1 className="bg-gradient-to-r from-pink-300 via-yellow-300 to-cyan-300 bg-clip-text text-transparent text-6xl font-black tracking-tighter mt-2">
          +{credits.toLocaleString()}
        </h1>
      </div>
    </div>
  );
}

export default function CreditStore() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [celebration, setCelebration] = useState(0);
  const pollTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: cat }, { data: w }] = await Promise.all([
        api.get("/credits/packages"),
        api.get("/gifts/catalog"),
        api.get("/wallet/me"),
      ]);
      setPackages(p.packages);
      // Pick featured gifts from catalog
      const all = Object.values(cat.collections).flat();
      setFeatured(FEATURED_GIFT_IDS.map((id) => all.find((g) => g.gift_id === id)).filter(Boolean));
      setBalance(w.wallet?.credits || 0);
    } catch (e) {
      toast.error("Couldn't load store");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ---- Poll Stripe status after redirect back from Checkout ----
  const sessionId = params.get("session_id");
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/credits/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success(`Credits added: +${data.credits.toLocaleString()}`);
          setCelebration(data.credits);
          setParams({}, { replace: true });
          await load();
          return;
        }
        if (data.status === "expired" || attempts > 12) {
          toast.error("Payment didn't complete");
          setParams({}, { replace: true });
          return;
        }
        pollTimer.current = setTimeout(poll, 2200);
      } catch (e) {
        toast.error("Couldn't verify payment");
        setParams({}, { replace: true });
      }
    };
    poll();
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const purchase = async (pkg) => {
    setPurchasing(pkg.package_id);
    try {
      const { data } = await api.post("/credits/purchase", {
        package_id: pkg.package_id,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
    } finally {
      setPurchasing(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 pb-24 space-y-8" data-testid="credits-page">
      {celebration > 0 && <PurchaseCelebration credits={celebration} onDone={() => setCelebration(0)} />}

      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white"
        data-testid="credits-back"
      >
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      {/* Hero with balance + value prop */}
      <header className="relative overflow-hidden rounded-3xl border border-pink-400/30 bg-gradient-to-br from-pink-500/15 via-zinc-950 to-cyan-500/15 p-6 md:p-8">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-pink-500/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-cyan-500/20 blur-3xl pointer-events-none" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="inline-flex items-center gap-2 text-pink-300 text-[10px] uppercase tracking-[0.3em] font-black">
              <Sparkles className="w-3 h-3" /> Credit Store
            </p>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter mt-2 leading-none">
              Buy credits.<br /><span className="bg-gradient-to-r from-pink-300 to-cyan-300 bg-clip-text text-transparent">Send love.</span>
            </h1>
            <p className="text-sm text-zinc-300 mt-3 max-w-md">
              Credits power animated gifts on Hidden Gem. Send a Burger to a vendor, drop a Diamond Crown on a streamer, or unleash the <span className="font-bold text-yellow-300">Hidden Feast</span> 🎉.
            </p>
          </div>
          <div className="shrink-0 rounded-2xl bg-zinc-950/70 border border-yellow-300/30 p-4 min-w-[180px]" data-testid="credits-balance-card">
            <p className="text-[10px] uppercase tracking-widest text-yellow-300 font-black">Your balance</p>
            <p className="text-3xl font-black tracking-tighter inline-flex items-center gap-1 mt-1">
              <Coins className="w-6 h-6 text-yellow-300" />
              {balance.toLocaleString()}
            </p>
            <button
              onClick={() => navigate("/wallet")}
              className="mt-1 text-[10px] uppercase tracking-widest font-bold text-zinc-300 hover:text-white inline-flex items-center gap-1"
              data-testid="credits-open-wallet"
            >
              <Wallet className="w-3 h-3" /> Open wallet
            </button>
          </div>
        </div>
      </header>

      {/* Legal / refund disclosure — user must see before choosing a pack */}
      <div className="rounded-2xl border border-yellow-300/20 bg-yellow-300/[0.03] p-4 text-xs text-yellow-100/90 space-y-1" data-testid="credit-disclosure">
        <p className="font-black text-yellow-200 uppercase tracking-widest text-[10px]">Before you buy credits</p>
        <p>
          Each Hidden Gem credit is worth <b>$0.01</b>. The <b>Hidden Gem Service Fee (7% + $0.50)</b> is added on top of every pack and is <b>not</b> Stripe's fee.
        </p>
        <p>
          All Hidden Gem Credit purchases are final and non-refundable, except where required by applicable law or payment-network rules. Purchased Credits cannot be directly withdrawn or redeemed for cash.
        </p>
      </div>

      {/* Featured Gifts marquee */}
      {!loading && featured.length > 0 && (
        <section data-testid="credits-featured-gifts">
          <h2 className="text-[10px] uppercase tracking-[0.3em] font-black text-pink-300 mb-3">Popular gifts</h2>
          <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4 pb-2">
            {featured.map((g) => (
              <div
                key={g.gift_id}
                className={`shrink-0 w-32 rounded-2xl border border-white/10 bg-white/[0.04] p-3 grid place-items-center text-center gap-1 rarity-${g.rarity}`}
              >
                <GiftIcon gift={g} size={48} />
                <p className="text-xs font-bold truncate w-full">{g.name}</p>
                <p className="text-[10px] text-yellow-300 font-black inline-flex items-center gap-0.5">
                  <Coins className="w-2.5 h-2.5" /> {g.cost_credits.toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Packages grid */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <h2 className="text-2xl font-black tracking-tighter">Credit packs</h2>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Apple Pay · Google Pay · Card</p>
        </div>
        {loading ? (
          <p className="text-center text-zinc-500 py-10">Loading…</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="credit-packs-grid">
            {packages.map((pkg) => {
              const total = pkg.credits;
              const isHero = pkg.tag === "Most Popular" || pkg.tag === "Best Value";
              const subtotal = ((pkg.subtotal_cents ?? Math.round(pkg.price_usd * 100)) / 100).toFixed(2);
              const fee = ((pkg.service_fee_cents ?? 0) / 100).toFixed(2);
              const grand = ((pkg.total_cents ?? Math.round(pkg.price_usd * 100)) / 100).toFixed(2);
              return (
                <button
                  key={pkg.package_id}
                  onClick={() => purchase(pkg)}
                  disabled={purchasing === pkg.package_id}
                  className={`relative text-left rounded-3xl p-4 border transition-all hover:scale-[1.02] active:scale-95 ${
                    isHero
                      ? "border-pink-400/50 bg-gradient-to-br from-pink-500/15 via-zinc-950 to-cyan-500/10 shadow-[0_0_28px_-10px_rgba(236,72,153,.7)]"
                      : "border-white/10 bg-white/[0.04] hover:border-pink-400/30"
                  }`}
                  data-testid={`pack-${pkg.package_id}`}
                >
                  {pkg.tag && (
                    <span className="absolute -top-2 left-3 text-[9px] uppercase tracking-widest font-black bg-pink-500 text-white rounded-full px-2 py-0.5 shadow-md">
                      {pkg.tag}
                    </span>
                  )}
                  <div className="flex items-center gap-1 text-yellow-300">
                    <Coins className="w-5 h-5" />
                    <span className="text-2xl font-black tracking-tighter text-white">{total.toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-zinc-400 mt-1">{pkg.label}</p>
                  <div className="mt-3 pt-3 border-t border-white/10 space-y-0.5 text-[11px]">
                    <div className="flex justify-between text-zinc-400">
                      <span>Credits</span>
                      <span className="tabular-nums">${subtotal}</span>
                    </div>
                    <div className="flex justify-between text-zinc-400" data-testid={`pack-fee-${pkg.package_id}`}>
                      <span>Hidden Gem Service Fee</span>
                      <span className="tabular-nums">${fee}</span>
                    </div>
                    <div className="flex justify-between pt-1 mt-1 border-t border-white/10">
                      <span className="text-white font-black">Total</span>
                      <span className="tabular-nums text-base font-black" data-testid={`pack-total-${pkg.package_id}`}>${grand}</span>
                    </div>
                  </div>
                  {purchasing === pkg.package_id && (
                    <p className="text-[10px] text-pink-300 mt-1">Opening Stripe…</p>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Trust + social proof */}
      <section className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-2xl font-black text-pink-300">70%</p>
            <p className="text-[10px] uppercase tracking-widest text-zinc-400">Goes to creator</p>
          </div>
          <div>
            <p className="text-2xl font-black text-cyan-300">5</p>
            <p className="text-[10px] uppercase tracking-widest text-zinc-400">Gift collections</p>
          </div>
          <div>
            <p className="text-2xl font-black text-yellow-300">47</p>
            <p className="text-[10px] uppercase tracking-widest text-zinc-400">Animated gifts</p>
          </div>
        </div>
        <p className="text-center text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-4">
          Powered by Stripe · Apple Pay · Google Pay · cards
        </p>
      </section>
    </div>
  );
}
